import {BoxElement, saveElement, updateElement} from "../../../reducers/Box/BoxReducer"
import {useAppDispatch, useAppSelector} from "../../../reducers/hooks"
import React, {useEffect} from "react"
import {FieldArray, Form, FormikProvider, useFormik} from "formik"
import * as Yup from "yup"
import DialogTitle from "@mui/material/DialogTitle"
import Divider from "@mui/material/Divider"
import {
    Autocomplete,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Grid,
    IconButton,
    TextField
} from "@mui/material"
import Stack from "@mui/material/Stack"
import InputLabel from "@mui/material/InputLabel"
import {Add, BoxRemove} from "iconsax-react"
import Button from "@mui/material/Button"
import {getAllElementShedNotPageable} from "../../../reducers/Shed/ShedReducer"
import {getAllDogWithoutBox} from "../../../reducers/Dog/DogReducer"
import {FormattedMessage, useIntl} from "react-intl"
import {ColumnFiltersState} from "@tanstack/react-table";

interface BoxFormProps {
    closeModal: () => void;
    element: BoxElement | null;
    onSuccess?: () => void;
    pageableData?: {
        page: number;
        rowsPerPage: number;
        filters: ColumnFiltersState | null;
    };
}

export const BoxFormElement: React.FC<BoxFormProps> = ({
                                                           closeModal,
                                                           element,
                                                           onSuccess,
                                                           pageableData
                                                       }) => {
    const dispatch = useAppDispatch()
    const shedList = useAppSelector((state) => state.shedElement.elements) || []
    const intl = useIntl()
    const shedMap = React.useMemo(
        () => new Map(shedList.map((shed) => [shed._id, shed])),
        [shedList]
    )

    const dogList = useAppSelector((state) => state.DogElement.elements) || []

    const formik = useFormik({
        initialValues: element
            ? {
                _id: element?._id || '',
                name: element?.name || '',
                maxCapacity: element?.maxCapacity || 0,
                dogIntoBox: element?.dogIntoBox || 0,
                isMaintenance: element?.isMaintenance || false,
                isAvailable: element?.isAvailable || false,
                addDogId: (element.addDogId || []).map(addDog => ({
                    _id: addDog._id,
                    name: addDog.name,
                    microchipCode: addDog.microchipCode
                })),

                shedId: element?.shedId || null,
                shedName: element?.shedName || null
            }
            : {
                _id: '',
                name: '',
                maxCapacity: 0,
                dogIntoBox: 0,
                isMaintenance: false,
                isAvailable: false,
                addDogId: [],
                shedId: null,
                shedName: null
            },
        validationSchema: Yup.object({
            name: Yup.string()
                .min(1, intl.formatMessage({id: "Length not valid, min 1 characters"}))
                .required(intl.formatMessage({id: "The name is obligatory"})),
            maxCapacity: Yup.number()
                .min(1, intl.formatMessage({id: "min 1"}))
                .required(intl.formatMessage({id: "The max capacity is required"})),
            isMaintenance: Yup.boolean(),
            isAvailable: Yup.boolean(),
            addDogId: Yup.array().of(Yup.object().shape({
                microchipCode: Yup.string().required("The microchipCode is required")
            })),
            shedId: Yup.object().required(intl.formatMessage({id: "The shed is required"}))
        }),

        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                const updaload = {
                    ...values,
                    shedId: values.shedId,
                    addDogId: values.addDogId,
                }
                if (element && element._id) {
                    dispatch(updateElement({
                        box: updaload,
                        callback: () => {
                            closeModal();
                            onSuccess?.();
                        }
                    }));
                } else {
                    dispatch(saveElement({
                        box: updaload,
                        callback: () => {
                            closeModal();
                            onSuccess?.();
                        }
                    }));
                }
                closeModal()

            } catch (e) {
                console.error('Error', e)
            }
        },
    })

    useEffect(() => {
        dispatch(getAllElementShedNotPageable())
        dispatch(getAllDogWithoutBox())

    }, [])

    const selectedShed = shedList.find(shed => shed._id === formik.values.shedId?._id) || null;

    return (
        <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                <DialogTitle>{intl.formatMessage({id: "NewBox"})}</DialogTitle>
                <Divider/>
                <DialogContent sx={{p: 2.5}}>
                    <Grid container spacing={3}>

                        {/* Name Field */}
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1}>
                                <InputLabel htmlFor="name"><FormattedMessage id="Name"
                                                                             defaultMessage="Name"/>:</InputLabel>
                                <TextField
                                    fullWidth
                                    id="name"
                                    placeholder={intl.formatMessage({id: "Insert the name here"})}
                                    {...formik.getFieldProps("name")}
                                    error={Boolean(formik.touched.name && formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                            </Stack>
                        </Grid>

                        {/* Max Capacity Field */}
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1}>
                                <InputLabel htmlFor="maxCapacity">
                                    <FormattedMessage id="MaxCapacity" defaultMessage="MaxCapacity"/>:
                                </InputLabel>
                                <TextField
                                    type="number"
                                    fullWidth
                                    id="maxCapacity"
                                    placeholder="Insert the max capacity"
                                    {...formik.getFieldProps("maxCapacity")}
                                    error={Boolean(formik.touched.maxCapacity && formik.errors.maxCapacity)}
                                    helperText={formik.touched.maxCapacity && formik.errors.maxCapacity}
                                />
                            </Stack>
                        </Grid>

                        {/* Maintenance Checkbox */}
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1}>
                                <InputLabel htmlFor="isMaintenance">
                                    <FormattedMessage id="Maintenance" defaultMessage="Maintenance"/>
                                </InputLabel>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="isMaintenance"
                                            checked={formik.values.isMaintenance}
                                            onChange={(e) => formik.setFieldValue("isMaintenance", e.target.checked)}
                                            name="isMaintenance"
                                            color="primary"
                                        />
                                    }
                                    label={<FormattedMessage id="Maintenance" defaultMessage="Maintenance"/>}
                                />
                            </Stack>
                        </Grid>

                        {/* Availability Checkbox */}
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1}>
                                <InputLabel htmlFor="isAvailable">
                                    <FormattedMessage id="isAvailable" defaultMessage="isAvailable"/>
                                </InputLabel>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="isAvailable"
                                            checked={formik.values.isAvailable}
                                            onChange={(e) => formik.setFieldValue("isAvailable", e.target.checked)}
                                            name="isAvailable"
                                            color="primary"
                                        />
                                    }
                                    label={<FormattedMessage id="isAvailable" defaultMessage="isAvailable"/>}
                                />
                            </Stack>
                        </Grid>

                        {/* Shed Selection */}
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1}>
                                <InputLabel htmlFor="shedId">
                                    <FormattedMessage id="Shed" defaultMessage="Shed"/>
                                </InputLabel>
                                <Autocomplete
                                    id="shedId"
                                    value={selectedShed || undefined}
                                    onChange={(e, newValue) => {
                                        formik.setFieldValue("shedId", newValue || null)
                                    }}
                                    options={shedList}
                                    getOptionLabel={(option) => option.name || ''}
                                    isOptionEqualToValue={(option, value) => option._id === value?._id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={<FormattedMessage id="Select Shed" defaultMessage="Select Shed"/>}
                                            error={Boolean(formik.touched.shedId && formik.errors.shedId)}
                                            helperText={formik.touched.shedId && formik.errors.shedId}
                                        />
                                    )}
                                    disableClearable
                                    noOptionsText={<FormattedMessage id="No sheds found"
                                                                     defaultMessage="No sheds found"/>}
                                />
                            </Stack>
                        </Grid>

                        {/* Add Dog Array */}
                        <Grid item xs={12}>
                            <FieldArray name="addDogId">
                                {({push, remove}) => (
                                    <Stack spacing={2}>
                                        <InputLabel>
                                            <FormattedMessage id="Dogs" defaultMessage="Dogs"/>
                                        </InputLabel>

                                        {formik.values.addDogId.length > 0 &&
                                            formik.values.addDogId.map((dog, index) => {
                                                return (
                                                    <Stack direction="row" spacing={2} alignItems="center" key={index}>
                                                        <Autocomplete
                                                            fullWidth
                                                            id={`addDogId[${index}].notes`}
                                                            value={formik.values.addDogId[index]} // Valore attuale del cane
                                                            onChange={(e, newValue) => {
                                                                if (newValue) {
                                                                    const updatedDogs = [...formik.values.addDogId];
                                                                    updatedDogs[index] = {
                                                                        _id: newValue._id,
                                                                        name: newValue.name,
                                                                        microchipCode: newValue.microchipCode,
                                                                    };
                                                                    formik.setFieldValue("addDogId", updatedDogs);
                                                                }
                                                            }}
                                                            options={dogList.filter((dog) =>
                                                                !formik.values.addDogId.some((selectedDog) => selectedDog._id === dog._id)
                                                            )}
                                                            filterOptions={(options, state) =>
                                                                options.filter((option) => {
                                                                    const label = option.microchipCode || option._id || '';
                                                                    return label.toLowerCase().startsWith(state.inputValue.toLowerCase());
                                                                })
                                                            }
                                                            getOptionLabel={(option) => option.microchipCode || option._id || ''}
                                                            isOptionEqualToValue={(option, value) => value ? option._id === value._id : false}

                                                            // disabled={Boolean(element && dog._id)}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={<FormattedMessage id="Select Dog"
                                                                                             defaultMessage="Select Dog"/>}
                                                                    error={Boolean(
                                                                        formik.touched.addDogId &&
                                                                        formik.errors.addDogId &&
                                                                        formik.errors.addDogId[index]
                                                                    )}
                                                                />
                                                            )}

                                                        />
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => remove(index)}
                                                            disabled={formik.values.addDogId.length === 0}
                                                        >
                                                            <BoxRemove/>
                                                        </IconButton>
                                                    </Stack>
                                                )
                                            })}

                                        <Button
                                            variant="outlined"
                                            startIcon={<Add/>}
                                            onClick={() => push({
                                                id: '',
                                                name: '',
                                                microchipCode: ''
                                            })} // Add a placeholder Dog object
                                        >
                                            <FormattedMessage id="Add Dog" defaultMessage="Add Dog"/>
                                        </Button>
                                    </Stack>
                                )}
                            </FieldArray>

                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{p: 2.5}}>
                    <Grid container justifyContent="flex-end" alignItems="center">
                        <Grid item>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button color="error" onClick={closeModal}>
                                    <FormattedMessage id="Cancel" defaultMessage="Cancel"/>
                                </Button>
                                <Button type="submit" variant="contained">
                                    <FormattedMessage id="Save" defaultMessage="Save"/>
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Form>
        </FormikProvider>
    )
}
